import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../context/UserContext';
import {
  getScenariosForStudent, updateCurrentStep, logStepAttempt,
  recordCompletion, getStudentCompletions,
} from '../services/dailyLivingService';
import { checkAndAwardAchievements } from '../services/activityService';
import { ttsSupported } from '../utils/voiceUtils';
import './Activity.css';
import './DailyLivingActivity.css';

const HINT_MESSAGES = {
  1: 'Listen carefully and look again!',
  2: 'Look for the picture that matches best!',
  3: "Here's the one to pick!",
};

const DailyLivingActivity = ({ onComplete }) => {
  const { currentUser } = useUser();

  const [scenarios, setScenarios]     = useState([]);
  const [completions, setCompletions] = useState([]);
  const [isLoading, setIsLoading]     = useState(true);

  const [activeScenario, setActiveScenario] = useState(null);
  const [currentStep, setCurrentStep]       = useState(1);
  const [wrongAttempts, setWrongAttempts]   = useState(0);
  const [answered, setAnswered]             = useState(false);
  const [shakeChoice, setShakeChoice]       = useState(null);
  const [isSpeaking, setIsSpeaking]         = useState(false);
  const [justCompleted, setJustCompleted]   = useState(false);

  const hasNavigatedRef = useRef(false);

  const loadAll = () => {
    if (!currentUser) return;
    setIsLoading(true);
    Promise.all([
      getScenariosForStudent(currentUser.id),
      getStudentCompletions(currentUser.id),
    ]).then(([scenariosRes, completionsRes]) => {
      setScenarios(scenariosRes.data || []);
      setCompletions(completionsRes.data || []);
      setIsLoading(false);
    });
  };

  useEffect(() => { loadAll(); }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const hintTier = Math.min(wrongAttempts, 3);
  const step     = activeScenario ? activeScenario.steps[currentStep - 1] : null;

  useEffect(() => {
    if (!hasNavigatedRef.current || !step || !step.voice_prompt || !ttsSupported) return;
    const timer = setTimeout(() => playInstruction(), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScenario, currentStep]);

  const playInstruction = () => {
    if (!step || !ttsSupported) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(step.instruction);
    utterance.lang    = 'en-US';
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend   = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  const openScenario = (scenario) => {
    const sortedSteps = [...scenario.steps].sort((a, b) => a.order - b.order);
    hasNavigatedRef.current = false;
    setActiveScenario({ ...scenario, steps: sortedSteps });
    setCurrentStep(scenario.current_step || 1);
    setWrongAttempts(0);
    setAnswered(false);
    setShakeChoice(null);
    setJustCompleted(false);
  };

  const backToList = () => {
    window.speechSynthesis.cancel();
    setActiveScenario(null);
    loadAll();
  };

  const handleAnswer = (optionIndex) => {
    if (answered || !currentUser) return;

    if (optionIndex === step.correct_option) {
      setAnswered(true);
      logStepAttempt(currentUser.id, activeScenario.id, step.order, true, wrongAttempts + 1, hintTier);
      window.speechSynthesis.cancel();
      if (ttsSupported) {
        const praise = new SpeechSynthesisUtterance('Great job!');
        praise.lang = 'en-US';
        window.speechSynthesis.speak(praise);
      }
    } else {
      const next = wrongAttempts + 1;
      setWrongAttempts(next);
      setShakeChoice(optionIndex);
      setTimeout(() => setShakeChoice(null), 500);
    }
  };

  const handleNextStep = async () => {
    window.speechSynthesis.cancel();
    const isLastStep = currentStep >= activeScenario.steps.length;

    if (isLastStep) {
      await recordCompletion(currentUser.id, activeScenario.id);
      await checkAndAwardAchievements(currentUser.id);
      setJustCompleted(true);
      setAnswered(false);
      setWrongAttempts(0);
      return;
    }

    const next = currentStep + 1;
    await updateCurrentStep(currentUser.id, activeScenario.id, next);
    hasNavigatedRef.current = true;
    setCurrentStep(next);
    setWrongAttempts(0);
    setAnswered(false);
    setShakeChoice(null);
  };

  const handleReplay = () => {
    hasNavigatedRef.current = false;
    setJustCompleted(false);
    setCurrentStep(1);
    setWrongAttempts(0);
    setAnswered(false);
    setShakeChoice(null);
  };

  if (isLoading) {
    return (
      <div className="activity-container">
        <div className="activity-header daily-living">
          <h2><i className="fas fa-hands-wash" aria-hidden="true"></i> Daily Living Skills</h2>
        </div>
        <div className="activity-content">
          <p style={{ textAlign: 'center', padding: '2rem' }}>Loading scenarios...</p>
        </div>
      </div>
    );
  }

  if (!activeScenario && scenarios.length === 0) {
    return (
      <div className="activity-container">
        <div className="activity-header daily-living">
          <h2><i className="fas fa-hands-wash" aria-hidden="true"></i> Daily Living Skills</h2>
        </div>
        <div className="activity-content">
          <p style={{ textAlign: 'center', padding: '2rem' }}>
            No Daily Living scenarios assigned yet. Ask your teacher to assign one!
          </p>
        </div>
      </div>
    );
  }

  if (!activeScenario) {
    return (
      <div className="activity-container">
        <div className="activity-header daily-living">
          <h2><i className="fas fa-hands-wash" aria-hidden="true"></i> Daily Living Skills</h2>
          <p>Practice everyday skills, one step at a time!</p>
        </div>
        <div className="activity-content">
          <div className="dl-scenario-list" role="list">
            {scenarios.map((s) => {
              const timesCompleted = completions.filter((c) => c.scenario_id === s.id).length;
              const inProgress = s.current_step > 1 && s.current_step <= s.steps.length;
              return (
                <button
                  key={s.id}
                  className="dl-scenario-card"
                  role="listitem"
                  onClick={() => openScenario(s)}
                >
                  <span className="dl-scenario-icon">
                    <i className={s.steps[0]?.image || 'fa-star'} aria-hidden="true"></i>
                  </span>
                  <span className="dl-scenario-info">
                    <span className="dl-scenario-title">{s.title}</span>
                    <span className="dl-scenario-meta">
                      {inProgress
                        ? `Resume at step ${s.current_step} of ${s.steps.length}`
                        : `${s.steps.length} steps`}
                      {timesCompleted > 0 && (
                        <span className="dl-scenario-completed">
                          {' '}Completed {timesCompleted}x
                        </span>
                      )}
                    </span>
                  </span>
                  <i className="fas fa-chevron-right dl-scenario-arrow" aria-hidden="true"></i>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (justCompleted) {
    return (
      <div className="activity-container">
        <div className="activity-header daily-living">
          <h2><i className="fas fa-hands-wash" aria-hidden="true"></i> Daily Living Skills</h2>
        </div>
        <div className="activity-content" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏆</div>
          <p style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Great work!
          </p>
          <p style={{ color: '#555', marginBottom: '2rem' }}>
            You finished all {activeScenario.steps.length} steps of {activeScenario.title}!
          </p>
          <div className="controls">
            <button className="btn btn-secondary" onClick={handleReplay}>
              <i className="fas fa-redo" aria-hidden="true"></i> Do It Again
            </button>
            <button className="btn btn-accent" onClick={() => { backToList(); if (onComplete) onComplete(); }}>
              <i className="fas fa-chart-bar" aria-hidden="true"></i> See My Progress
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isLastStep = currentStep >= activeScenario.steps.length;

  return (
    <div className="activity-container">
      <div className="activity-header daily-living">
        <h2><i className="fas fa-hands-wash" aria-hidden="true"></i> {activeScenario.title}</h2>
        <button className="dl-back-btn" onClick={backToList} aria-label="Back to scenario list">
          <i className="fas fa-arrow-left" aria-hidden="true"></i> All Scenarios
        </button>
      </div>

      <div className="activity-content">

        <div className="dl-step-indicator" aria-live="polite">
          Step {currentStep} of {activeScenario.steps.length}
        </div>

        <div className="dl-step-visual" aria-label={step.instruction}>
          <i className={step.image} aria-hidden="true"></i>
        </div>

        <div className="dl-instruction" aria-live="polite">
          {step.instruction}
        </div>

        {ttsSupported && (
          <div className="controls">
            <button
              className="btn btn-secondary"
              onClick={playInstruction}
              disabled={isSpeaking}
              aria-label="Hear the instruction read aloud"
            >
              <i className={isSpeaking ? 'fas fa-spinner fa-spin' : 'fas fa-volume-up'} aria-hidden="true"></i>
              {isSpeaking ? 'Speaking...' : 'Hear It Again'}
            </button>
          </div>
        )}

        {wrongAttempts > 0 && !answered && (
          <div className={`dl-hint dl-hint-tier-${hintTier}`} role="status" aria-live="polite">
            <i className="fas fa-lightbulb" aria-hidden="true"></i> {HINT_MESSAGES[hintTier] || HINT_MESSAGES[3]}
          </div>
        )}
        {answered && (
          <div className="dl-hint dl-hint-success" role="status" aria-live="polite">
            <i className="fas fa-check-circle" aria-hidden="true"></i> Correct! Great job!
          </div>
        )}

        <div className="dl-choices" role="group" aria-label="Answer choices">
          {step.options.map((option, index) => {
            let cls = 'dl-choice-btn';
            const isCorrectOption = index === step.correct_option;
            if (answered && isCorrectOption) cls += ' correct';
            else if (answered && !isCorrectOption) cls += ' dimmed';
            else if (!answered && hintTier >= 3 && isCorrectOption) cls += ' hint-reveal';
            else if (!answered && hintTier >= 2 && isCorrectOption) cls += ' hint-glow';
            if (shakeChoice === index) cls += ' shake';
            return (
              <button
                key={index}
                className={cls}
                onClick={() => handleAnswer(index)}
                disabled={answered}
                aria-label={option}
              >
                {answered && isCorrectOption && <i className="fas fa-check" aria-hidden="true"></i>}
                {answered && !isCorrectOption && <i className="fas fa-times" aria-hidden="true"></i>}
                {' '}{option}
              </button>
            );
          })}
        </div>

        <div className="controls">
          <button
            className="btn btn-accent"
            onClick={handleNextStep}
            disabled={!answered}
          >
            <i className="fas fa-arrow-right" aria-hidden="true"></i>
            {isLastStep ? ' Finish' : ' Next Step'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default DailyLivingActivity;

